/**
 * Bottom navigation mobile (visible < lg uniquement).
 * Affiche 5 icones principales fixees en bas de l'ecran pour acces ultra-rapide
 * sans avoir a ouvrir le drawer. UX standard des apps natives.
 *
 * Les icones s'adaptent au role utilisateur (client = portail / collaborateur = team).
 */
import React from "react";
import { NavLink, useLocation } from "react-router-dom";

const ICONS = {
  dashboard: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  ),
  projects: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  ),
  delays: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
  artisans: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63" />
  ),
  more: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h7" />
  ),
  portail: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  ),
  documents: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  ),
  agenda: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  ),
  qualif: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  ),
  analyses: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  ),
};

const NavBtn = ({ to, label, icon, active, testid }) => (
  <NavLink to={to} data-testid={testid}
           className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors active:bg-gray-100 ${active ? 'text-[#008A8D]' : 'text-gray-500'}`}>
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {icon}
    </svg>
    <span className={`text-[10px] font-semibold leading-none ${active ? 'text-[#008A8D]' : 'text-gray-600'}`}>
      {label}
    </span>
  </NavLink>
);

const BottomNav = ({ user, onMoreClick }) => {
  const location = useLocation();
  if (!user) return null;
  const isClient = user.role === "client";

  const items = isClient
    ? [
        { to: "/portal", label: "Accueil", icon: ICONS.portail, key: "portail" },
        { to: "/portal?tab=documents", label: "Documents", icon: ICONS.documents, key: "documents" },
        { to: "/portal?tab=agenda", label: "Agenda", icon: ICONS.agenda, key: "agenda" },
        { to: "/portal?tab=messages", label: "Messages", icon: ICONS.delays, key: "messages" },
      ]
    : [
        { to: "/dashboard", label: "Cockpit", icon: ICONS.dashboard, key: "dashboard" },
        { to: "/qualification", label: "Qualifs", icon: ICONS.qualif, key: "qualification" },
        { to: "/analyses", label: "Analyses", icon: ICONS.analyses, key: "analyses" },
        { to: "/projects", label: "Projets", icon: ICONS.projects, key: "projects" },
      ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-30 lg:hidden shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      data-testid="bottom-nav"
    >
      {items.map((it) => (
        <NavBtn key={it.key} to={it.to} label={it.label} icon={it.icon}
                active={isActive(it.to.split('?')[0])}
                testid={`bottomnav-${it.key}`} />
      ))}
      <button
        onClick={onMoreClick}
        className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2 active:bg-gray-100 text-gray-500"
        data-testid="bottomnav-more"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {ICONS.more}
        </svg>
        <span className="text-[10px] font-semibold leading-none text-gray-600">Plus</span>
      </button>
    </nav>
  );
};

export default BottomNav;
